<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 flex items-end"
      @click.self="$emit('close')"
      :style="{ zIndex: zIndex, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }"
    >
      <div
        ref="contentRef"
        @scroll="onScroll"
        :class="['w-full overflow-y-auto mx-auto sheet',
                 expanded ? 'sheet-expanded' : 'rounded-t-3xl']"
        :style="{ background: '#FAF3E8', maxWidth: '440px',
                  overscrollBehavior: 'contain', transition: 'max-height .3s, border-radius .3s' }"
      >
        <!-- Sticky header: название + крестик -->
        <div class="sticky top-0 z-10 px-5 pt-5 pb-2 flex items-start justify-between gap-3"
             :style="{ background: '#FAF3E8', paddingTop: expanded ? 'calc(20px + var(--safe-top))' : undefined }">
          <div class="flex-1 min-w-0">
            <h2 class="text-20 font-semibold text-ink leading-snug">
              {{ product.name }}
            </h2>
            <p v-if="product.brand" class="text-14 text-ink/70 mt-0.5 truncate">
              {{ product.brand }}
            </p>
          </div>
          <button @click="$emit('close')"
                  class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors shrink-0 mt-0.5">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="px-5 pb-8">
          <!-- Вес -->
          <div class="flex items-baseline gap-1.5 mb-4">
            <span class="text-15 text-ink font-medium">На</span>
            <input
              :value="weightStr"
              @input="onWeightInput"
              type="text"
              inputmode="decimal"
              class="w-16 text-center text-17 font-semibold text-ink bg-white rounded-lg px-1 py-0.5 outline-none focus:ring-0"
            />
            <span class="text-15 text-ink font-medium">г</span>
          </div>

          <!-- MacroBars -->
          <div class="mb-4">
            <MacroBar label="Ккал"      :value="scaled.kcal"    unit="ккал" :upper="2000"               color="#EE998C" precise />
            <MacroBar label="Белки"     :value="scaled.protein" unit="г"    :upper="120" :lower="80"    color="#9FC2C1" precise />
            <MacroBar label="Жиры"      :value="scaled.fat"     unit="г"    :upper="100" :lower="50"    color="#EBC06D" precise />
            <MacroBar label="Углеводы"  :value="scaled.carbs"   unit="г"    :upper="300" :lower="200"   color="#EEAD77" precise />
            <MacroBar label="Клетчатка" :value="scaled.fiber"   unit="г"    :upper="35"  :lower="25"    color="#A6B779" precise :no-data="noMicroData && scaled.fiber == null" :approx="product.fiber_source === 'ai'" />
            <MacroBar label="Вода"      :value="scaled.water"   unit="г"    :upper="3000"               color="#ABDEEB" :no-norm="true" normText="из еды и напитков" precise :no-data="noMicroData && !scaled.water" />
          </div>

          <!-- VitaminBlock -->
          <VitaminBlock :totals="scaledVitamins" :sex="sex" :age="age" :default-expanded="true" :no-data="noMicroData" />

          <!-- Подробный состав (extra nutrients) -->
          <div v-if="scaledExtra.length > 0" class="mt-4">
            <p class="text-13 font-semibold text-ink/70 mb-2">Подробный состав</p>
            <div class="rounded-xl bg-white/60 divide-y divide-[#E0D9CF]/50">
              <div v-for="item in scaledExtra" :key="item.key"
                   class="flex items-center justify-between px-3 py-1.5">
                <span class="text-13 text-ink/80">{{ item.label }}</span>
                <span class="text-13 font-medium text-ink">{{ item.display }}</span>
              </div>
            </div>
          </div>

          <!-- Состав (ingredients_text) -->
          <div v-if="product.ingredients_text" class="mt-4">
            <p class="text-13 font-semibold text-ink/70 mb-1">Состав</p>
            <p :class="['text-13 text-ink/70 leading-relaxed',
                        !ingredientsExpanded && isLongIngredients ? 'line-clamp-3' : '']">
              {{ product.ingredients_text }}
            </p>
            <button v-if="isLongIngredients"
                    @click="ingredientsExpanded = !ingredientsExpanded"
                    class="text-12 text-ink/70 underline mt-0.5">
              {{ ingredientsExpanded ? 'Свернуть' : 'Показать полностью' }}
            </button>
          </div>

          <!-- Источник + штрихкод -->
          <div class="mt-4 flex items-baseline justify-between gap-2">
            <p v-if="product.source" class="text-12 text-ink/70 leading-snug">
              {{ product.source === 'user' ? 'Добавлено пользователем приложения' : product.source }}
            </p>
            <p v-if="product.barcode" class="text-12 text-ink/70 leading-snug shrink-0">
              {{ product.barcode }}
            </p>
          </div>

          <!-- Кнопка редактирования (только для автора продукта) -->
          <button
            v-if="product.is_own"
            @click="showEditDialog = true"
            class="mt-4 w-full py-3 rounded-2xl bg-ink text-cream text-15 font-semibold active:scale-[0.98] transition-all"
          >
            Редактировать
          </button>
        </div>
      </div>
    </div>

    <!-- Диалог редактирования -->
    <LabelReviewDialog
      v-if="showEditDialog"
      v-model="showEditDialog"
      mode="edit"
      :product-id="product.id"
      :barcode="product.barcode || null"
      :initial-data="{
        name: product.name,
        brand: product.brand,
        per100: { kcal: product.kcal, protein: product.protein, fat: product.fat, carbs: product.carbs, fiber: product.fiber },
        ingredients_text: product.ingredients_text,
      }"
      @submitted="onEditSubmitted"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import MacroBar from './MacroBar.vue'
import VitaminBlock from './VitaminBlock.vue'
import LabelReviewDialog from './LabelReviewDialog.vue'

const EXTRA_LABELS = {
  sugars: 'Сахара',
  added_sugar: 'Добавленный сахар',
  salt: 'Соль',
  starch: 'Крахмал',
  cholesterol: 'Холестерин',
  saturated_fat: 'Насыщенные ЖК',
  mono_fat: 'Мононенасыщенные ЖК',
  poly_fat: 'Полиненасыщенные ЖК',
  organic_acids: 'Органические кислоты',
  ash: 'Зола',
}

const props = defineProps({
  product: { type: Object, required: true },
  sex:     { type: String, default: 'male' },
  age:     { type: Number, default: null },
  zIndex:  { type: Number, default: 100 },
})

const emit = defineEmits(['close', 'updated'])

const weight = ref(100)
const weightStr = ref('100')
function onWeightInput(e) {
  const raw = e.target.value.replace(',', '.')
  weightStr.value = e.target.value
  const num = parseFloat(raw)
  weight.value = isNaN(num) ? 0 : num
}
const ratio = computed(() => (weight.value || 0) / 100)
const showEditDialog = ref(false)

// Scroll expand
const contentRef = ref(null)
const expanded = ref(false)

function onScroll() {
  if (!contentRef.value || expanded.value) return
  // Разворачиваем один раз и обратно не схлопываем. Лист прижат к низу экрана:
  // когда он вырастает до 100vh, содержимое помещается целиком и scrollTop
  // обнуляется. При двусторонней привязке условие тут же становилось ложным,
  // лист возвращался к 85vh, прокрутка появлялась снова — и так по кругу.
  // Карточку мотало, а кнопка «Редактировать» внизу уезжала из-под пальца.
  // В PhotoRecognizeDialog эта защёлка стоит с самого начала.
  if (contentRef.value.scrollTop > 10) expanded.value = true
}

// Ingredients collapse
const ingredientsExpanded = ref(false)
const isLongIngredients = computed(() =>
  (props.product.ingredients_text || '').length > 150
)

// Scroll lock (skip if body is already locked by a parent dialog)
let savedScrollY = 0
let ownedScrollLock = false

onMounted(() => {
  if (document.body.style.position === 'fixed') {
    // Parent dialog already locked — don't touch
    ownedScrollLock = false
  } else {
    ownedScrollLock = true
    savedScrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.overflow = 'hidden'
  }
})

onUnmounted(() => {
  if (ownedScrollLock) {
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.overflow = ''
    window.scrollTo(0, savedScrollY)
  }
})

// Источник без микронутриентов (не Скурихин и не USDA)
const noMicroData = computed(() => {
  const s = (props.product.source || '').toLowerCase()
  return !s.includes('скурихин') && !s.includes('skurikhin') && !s.includes('usda')
})

const scale = (val) => val != null ? Math.round(val * ratio.value * 10) / 10 : 0

const scaled = computed(() => ({
  kcal:    scale(props.product.kcal),
  protein: scale(props.product.protein),
  fat:     scale(props.product.fat),
  carbs:   scale(props.product.carbs),
  fiber:   scale(props.product.fiber),
  water:   scale(props.product.water),
}))

const scaledVitamins = computed(() => {
  const v = props.product.vitamins || {}
  const result = {}
  for (const [key, val] of Object.entries(v)) {
    result[key] = Math.round(val * ratio.value * 100) / 100
  }
  return result
})

const scaledExtra = computed(() => {
  const extra = props.product.extra
  if (!extra) return []
  return Object.entries(extra)
    .filter(([, v]) => v?.value != null)
    .map(([key, v]) => {
      const scaled = Math.round(v.value * ratio.value * 100) / 100
      return {
        key,
        label: EXTRA_LABELS[key] || key,
        display: `${scaled} ${v.unit}`,
      }
    })
})

function onEditSubmitted(result) {
  showEditDialog.value = false
  emit('updated', result.product)
}
</script>

<style scoped>
/* Лист прижат к низу экрана, поэтому его высота не должна превышать видимую
   часть окна. В мобильном браузере 100vh выше видимой области на высоту
   адресной строки: верхний край листа уходил за экран вместе с липкой шапкой,
   и крестик становилось не нажать. dvh считает именно видимую часть, vh
   оставлен запасным для старых webview — так же сделано на экранах онбординга. */
.sheet {
  max-height: 85vh;
  max-height: 85dvh;
}
.sheet-expanded {
  max-height: 100vh;
  max-height: 100dvh;
}
</style>
